import { useState, useEffect } from 'react';
import { apiService } from '../../../services/api';

interface Milestone {
  month: string;
  target: string | null;
  actual: string | null;
  achieved: boolean;
  recorded_at: string | null;
}

interface SlabData {
  label: string;
  percentage: number;
  count: number;
  color: string;
}

interface Trend {
  value: number;
  direction: 'up' | 'down' | 'neutral';
  formatted: string;
}

interface Goal {
  id: number;
  goal_type: string;
  goal_category: string;
  title: string;
  icon: string;
  color: string;
  baseline_value: string;
  current_value: string;
  target_value: string;
  unit: string;
  target_type: string;
  progress_percentage: number;
  status: 'on_track' | 'paused' | 'completed' | 'at_risk';
  days_remaining: number;
  cycle_start_date: string;
  cycle_end_date: string;
  milestones: Milestone[];
  trend: Trend;
  slab_data: SlabData[] | null;
}

interface Performance {
  avg_progress_percentage: number;
  total_goals: number;
  goals_completed: number;
  goals_on_track: number;
  goals_in_progress: number;
  goals_paused: number;
  goals_at_risk: number;
}

interface CycleInfo {
  cycle_start_date: string;
  cycle_end_date: string;
  days_remaining: number;
  days_elapsed: number;
  total_days: number;
  progress_percentage: number;
}

interface GoalsTrackingProps {
  userId?: string;
  onTooltipShow: (title: string, content: string) => void;
}

export default function GoalsTracking({ userId, onTooltipShow }: GoalsTrackingProps) {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [performance, setPerformance] = useState<Performance | null>(null);
  const [cycleInfo, setCycleInfo] = useState<CycleInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get user ID from localStorage if not provided
  const effectiveUserId = userId || localStorage.getItem('user_id') || '';

  useEffect(() => {
    if (!effectiveUserId) {
      setLoading(false);
      setError('No user ID found');
      return;
    }
    fetchGoalsData();
  }, [effectiveUserId]);

  const fetchGoalsData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all data in parallel
      const [performanceRes, breakdownRes, cycleRes] = await Promise.all([
        apiService.getGovernanceGoalsPerformance(effectiveUserId),
        apiService.getGovernanceGoalsBreakdown(effectiveUserId),
        apiService.getGovernanceGoalsCycle(effectiveUserId)
      ]) as [any, any, any];

      // Set performance data
      if (performanceRes?.data) {
        setPerformance(performanceRes.data);
      }

      // Goals Order Configuration
      const GOAL_ORDER = [
        'Increase Organic Traffic',
        'Captured Visibility',
        'Capture Visibility Features', // backward-compat for existing DB rows
        'Improve Keyword Rankings',
        'Improve Domain Authority',
        'Reduce Average Position',
        'Increase Impressions'
      ];

      // Set goals breakdown with sorting
      if (breakdownRes?.data?.goals) {
        const sortedGoals = [...breakdownRes.data.goals].sort((a, b) => {
          const indexA = GOAL_ORDER.indexOf(a.title);
          const indexB = GOAL_ORDER.indexOf(b.title);

          // If both are in the list, sort by index
          if (indexA !== -1 && indexB !== -1) return indexA - indexB;

          // If only A is in the list, it comes first
          if (indexA !== -1) return -1;

          // If only B is in the list, it comes first
          if (indexB !== -1) return 1;

          // If neither, keep original order (or sort alphabetically if preferred)
          return 0;
        });
        setGoals(sortedGoals);
      }

      // Set cycle info
      if (cycleRes?.data) {
        setCycleInfo(cycleRes.data);
      }

    } catch (err) {
      console.error('Error fetching goals data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch goals data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on_track':
        return 'text-green-600 bg-green-100 border-green-200';
      case 'completed':
        return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'at_risk':
        return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'paused':
        return 'text-gray-600 bg-gray-100 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'on_track':
        return 'ri-check-line';
      case 'completed':
        return 'ri-check-double-line';
      case 'at_risk':
        return 'ri-error-warning-line';
      case 'paused':
        return 'ri-pause-circle-line';
      default:
        return 'ri-time-line';
    }
  };

  const formatStatusLabel = (status: string) => {
    return status.replace('_', ' ').toUpperCase();
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">Loading goals data...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-center py-8 text-red-500">
            <i className="ri-error-warning-line text-2xl mr-2"></i>
            <span>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  // No data state - Goals not configured
  if (goals.length === 0 || !performance || performance.total_goals === 0) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Goals Tracking</h2>
            <button
              onClick={() => onTooltipShow('Goals Tracking', 'Monitor the progress of your experimental 3-month goals. Track current performance against experimental targets and view monthly milestones.')}
              className="text-gray-400 hover:text-teal-500"
            >
              <i className="ri-information-line text-xl"></i>
            </button>
          </div>
          <p className="text-gray-600 dark:text-gray-400">Monitor your 3-month experimental goal progress and performance</p>
        </div>

        {/* Empty State Banner */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-10 shadow-lg border border-gray-200 dark:border-gray-700 text-center">
          <div className="w-16 h-16 bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-flag-line text-3xl"></i>
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Goals Configured in SEO Manager</h3>
          <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-6 text-sm">
            Goal Tracking will begin displaying progress once your 90-day SEO Strategy Goals have been initialized and configured under SEO Manager.
          </p>
          <a
            href="/dashboard#goal-setting"
            className="inline-flex items-center px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm transition-colors shadow-sm cursor-pointer"
          >
            <i className="ri-settings-4-line mr-2"></i>Go to Goal Setting
          </a>
        </div>
      </div>
    );
  }

  const overallProgress = performance?.avg_progress_percentage || 0;
  const daysRemaining = cycleInfo?.days_remaining || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Goals Tracking</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={fetchGoalsData}
              className="text-gray-400 hover:text-teal-500 transition-colors"
              title="Refresh data"
            >
              <i className="ri-refresh-line text-xl"></i>
            </button>
            <button
              onClick={() => onTooltipShow('Goals Tracking', 'Monitor the progress of your experimental 3-month goals. Track current performance against experimental targets and view monthly milestones.')}
              className="text-gray-400 hover:text-teal-500"
            >
              <i className="ri-information-line text-xl"></i>
            </button>
          </div>
        </div>
        <p className="text-gray-600 dark:text-gray-400">Monitor your 3-month experimental goal progress and performance</p>
      </div>

      {/* Overall Progress Summary */}
      <div className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between text-white mb-4">
          <div>
            <h3 className="text-xl font-bold mb-1">Overall Goal Progress</h3>
            <p className="text-teal-100">{daysRemaining} days remaining</p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold">{Math.round(overallProgress)}%</div>
            <div className="text-sm text-teal-100">Goal completion</div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mt-4">
          <div className="bg-white/20 rounded-lg p-3">
            <div className="text-2xl font-bold text-white">{performance?.total_goals || 0}</div>
            <div className="text-xs text-teal-100">Total Goals</div>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <div className="text-2xl font-bold text-white">{(performance?.goals_on_track || 0) + (performance?.goals_in_progress || 0)}</div>
            <div className="text-xs text-teal-100">In Progress</div>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <div className="text-2xl font-bold text-white">{performance?.goals_completed || 0}</div>
            <div className="text-xs text-teal-100">Completed</div>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <div className="text-2xl font-bold text-white">{performance?.goals_paused || 0}</div>
            <div className="text-xs text-teal-100">Paused</div>
          </div>
        </div>
      </div>

      {/* Goals Progress Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {goals.map((goal) => (
          <div
            key={goal.id}
            className={`rounded-xl p-6 shadow-lg border ${goal.status === 'paused'
              ? 'bg-gray-50 dark:bg-gray-900/20 border-gray-300 dark:border-gray-700'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
              }`}
          >
            {/* Goal Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${goal.color} flex items-center justify-center`}>
                  <i className={`${goal.icon} text-xl text-white`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">{goal.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{goal.days_remaining} days left</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center space-x-1 ${getStatusColor(goal.status)}`}>
                <i className={`${getStatusIcon(goal.status)}`}></i>
                <span>{formatStatusLabel(goal.status)}</span>
              </span>
            </div>

            {/* Progress Bar - Only for non-paused goals */}
            {goal.status !== 'paused' && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Progress</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">{Math.round(goal.progress_percentage)}%</span>
                </div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${goal.color} rounded-full transition-all duration-500`}
                    style={{ width: `${Math.min(goal.progress_percentage, 100)}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Keyword Ranking Slabs */}
            {goal.target_type === 'slabs' && goal.slab_data && goal.slab_data.length > 0 && (
              <div className="mb-4 space-y-3">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Keyword Ranking Distribution:</h4>
                {goal.slab_data.map((slab, idx) => (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-700 dark:text-gray-300">{slab.label}</span>
                      <span className="font-bold text-gray-900 dark:text-white">
                        {slab.percentage}% ({slab.count} keywords)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`${slab.color} h-2 rounded-full transition-all duration-300`}
                        style={{ width: `${slab.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Current vs Target - For non-slab goals */}
            {goal.target_type !== 'slabs' && (
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Baseline</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{goal.baseline_value}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{goal.unit}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{goal.current_value}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{goal.unit}</p>
                </div>
                <div className={`rounded-lg p-3 ${goal.status === 'paused'
                  ? 'bg-gray-200 dark:bg-gray-700'
                  : goal.target_type === 'not_applicable'
                    ? 'bg-green-50 dark:bg-green-900/30 border border-dashed border-green-300 dark:border-green-700'
                    : goal.target_type === 'range'
                      ? 'bg-orange-50 dark:bg-orange-900/30'
                      : 'bg-green-50 dark:bg-green-900/30'
                  }`}>
                  <p className={`text-xs mb-1 ${goal.status === 'paused'
                    ? 'text-gray-600 dark:text-gray-400'
                    : goal.target_type === 'range'
                      ? 'text-orange-700 dark:text-orange-400'
                      : 'text-green-700 dark:text-green-400'
                    }`}>Target</p>
                  <div className="flex items-center space-x-1">
                    {goal.target_type === 'growth' && goal.status !== 'paused' && (
                      <i className="ri-arrow-up-line text-green-600 text-sm"></i>
                    )}
                    {goal.target_type === 'range' && goal.status !== 'paused' && (
                      <i className="ri-arrow-down-line text-orange-600 text-sm"></i>
                    )}
                    {goal.status === 'paused' && (
                      <i className="ri-pause-circle-line text-gray-500 text-sm"></i>
                    )}
                    {goal.target_type === 'not_applicable' && (
                      <i className="ri-eye-line text-green-500 text-sm"></i>
                    )}
                    <p className={`text-lg font-bold ${goal.status === 'paused'
                      ? 'text-gray-600'
                      : goal.target_type === 'range'
                        ? 'text-orange-600'
                        : 'text-green-600'
                      }`}>{goal.target_type === 'not_applicable' ? 'N/A' : goal.target_value}</p>
                  </div>
                  <p className={`text-xs ${goal.status === 'paused'
                    ? 'text-gray-600 dark:text-gray-400'
                    : goal.target_type === 'range'
                      ? 'text-orange-700 dark:text-orange-400'
                      : 'text-green-700 dark:text-green-400'
                    }`}>{goal.target_type === 'not_applicable' ? 'Monitoring' : goal.unit}</p>
                </div>
              </div>
            )}

            {/* Trend - Only for non-paused goals */}
            {goal.status !== 'paused' && goal.trend && (
              <div className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg mb-4">
                <span className="text-sm text-gray-700 dark:text-gray-300">Trend</span>
                <span className={`text-sm font-bold flex items-center ${goal.trend.direction === 'up' ? 'text-green-600' :
                  goal.trend.direction === 'down' && goal.target_type === 'range' ? 'text-green-600' :
                    goal.trend.direction === 'down' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                  {goal.trend.direction === 'up' && <i className="ri-arrow-up-line mr-1"></i>}
                  {goal.trend.direction === 'down' && <i className="ri-arrow-down-line mr-1"></i>}
                  {goal.trend.formatted}
                </span>
              </div>
            )}

            {/* Monthly Milestones */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Monthly Milestones</h4>
              <div className="space-y-2">
                {goal.milestones.map((milestone, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-lg ${goal.status === 'paused'
                      ? 'bg-gray-100 dark:bg-gray-700'
                      : milestone.actual === null
                        ? 'bg-gray-50 dark:bg-gray-700'
                        : milestone.achieved
                          ? 'bg-green-50 dark:bg-green-900/30'
                          : 'bg-yellow-50 dark:bg-yellow-900/30'
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${goal.status === 'paused'
                        ? 'bg-gray-400'
                        : milestone.actual === null
                          ? 'bg-gray-300 dark:bg-gray-600'
                          : milestone.achieved
                            ? 'bg-green-500'
                            : 'bg-yellow-500'
                        }`}>
                        {goal.status === 'paused' ? (
                          <i className="ri-pause-line text-white text-xs"></i>
                        ) : milestone.actual !== null && (
                          <i className={`${milestone.achieved ? 'ri-check-line' : 'ri-time-line'} text-white text-xs`}></i>
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {milestone.actual !== null ? `${milestone.actual} ${goal.unit || ''}` : milestone.month}
                      </span>
                    </div>
                    <div className="text-right">
                      {milestone.actual === null && (
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">—</div>
                      )}
                      <div className={`text-xs ${milestone.actual !== null ? 'text-sm font-semibold text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                        Target: {milestone.target || '—'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
